<script setup lang="ts">
import { capitalize } from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'
import { VButtonInvisible, VSkeletonBar } from '@/modules/shared/components'
import RouterLinkBack from '@/modules/shared/components/router-link-back.vue'
import type { Entity } from '@/modules/investing/stores/better-entity-store'
import type { Role } from '@/modules/auth/stores/auth-store'
import { useCan } from '@/modules/auth/composables/use-can'
import { investorPath } from '@/modules/investing/utils/investor'
import { rails_url } from '@/modules/shared/utils/rails'
import { safeFormat } from '@/modules/shared/utils/date'

///////////////////////////////////////////////////////////////////////////////
// Utils
///////////////////////////////////////////////////////////////////////////////

function formattedEntityType(entity_type?: string) {
  switch (entity_type) {
    case 'fund':
      return 'FUND'
    case 'spv':
      return 'Special Purpose Vehicle'
    default:
      return null
  }
}

function backButtonLabel(entity_type: string) {
  switch (entity_type) {
    case 'fund':
      return 'Funds'
    case 'spv':
      return 'SPVs'
    default:
      return null
  }
}

///////////////////////////////////////////////////////////////////////////////
// Store-free (props only) so the component is easy to unit test. The parent
// (entity-layout) sources `entity` and `current_user_role` from the stores.
const props = withDefaults(
  defineProps<{
    entity?: Entity | null
    current_user_role: Role
    skeleton?: boolean
  }>(),
  { entity: null, skeleton: false },
)

const { t } = useI18n()
const route = useRoute()
const { can } = useCan()

const establishedDate = computed(() => safeFormat(props.entity?.date_established, 'MMM d, yyyy'))

const back_url_config = computed(() => {
  const { slug, entity_type, entity_id } = route.params
  const overview = { name: 'investing.entity-overview', params: { slug, entity_type, entity_id } }

  if (props.current_user_role === 'company_user') {
    const list = `${rails_url()}/${entity_type}s`
    return { to: list, fallback_url: list, label: '← Back' }
  }

  if (props.current_user_role === 'site_admin' || props.current_user_role === 'group_admin') {
    return {
      to: { name: 'investing.entities', params: { slug, entity_type: `${entity_type}s` } },
      fallback_url: overview,
      label: `← Back to all ${backButtonLabel(entity_type as string)}`,
    }
  }

  return { to: { name: 'dashboard' }, fallback_url: overview, label: '← Back' }
})
</script>

<template>
  <header
    class="-mt-4 mb-12 rounded-2xl bg-[#2a3f54] px-5 pb-0 pt-6 sm:-mx-12 sm:-mt-14 sm:rounded-none sm:px-12 sm:pt-10"
  >
    <div class="mb-4">
      <RouterLinkBack :to="back_url_config.to" :fallback="back_url_config.fallback_url">
        <VButtonInvisible class="text-slate-400 hover:!bg-white/10 hover:text-white active:!bg-white/20">
          {{ back_url_config.label }}
        </VButtonInvisible>
      </RouterLinkBack>
    </div>
    <div class="flex flex-col-reverse gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div v-if="skeleton" aria-hidden="true" class="flex flex-col gap-3">
        <VSkeletonBar width="64px" tone="dark" />
        <VSkeletonBar width="256px" height="24px" tone="dark" />
        <VSkeletonBar width="180px" tone="dark" />
      </div>
      <div v-else class="min-w-0">
        <p
          v-if="formattedEntityType(entity?.entity_type)"
          class="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 sm:text-xs"
        >
          {{ formattedEntityType(entity?.entity_type) }}
        </p>
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h2 class="break-words text-2xl font-bold leading-7 text-white sm:text-3xl">
            {{ entity?.name }}
          </h2>
          <slot name="badges"></slot>
        </div>
        <p v-if="entity?.legal_name && entity.legal_name !== entity.name" class="mt-1 text-sm text-slate-400">
          {{ entity.legal_name }}
        </p>
        <div
          v-if="establishedDate || entity?.tax_id || entity?.manager?.name"
          class="mt-3 flex flex-wrap items-center gap-x-2 text-sm text-slate-400"
        >
          <span v-if="establishedDate">
            {{ capitalize(t('shared.date established')) }}
            <span class="font-semibold text-white">{{ establishedDate }}</span>
          </span>
          <span v-if="establishedDate && entity?.tax_id" class="text-slate-500">·</span>
          <span v-if="entity?.tax_id">
            {{ t('shared.EIN') }} <span class="font-semibold text-white">{{ entity?.tax_id }}</span>
          </span>
          <span v-if="entity?.tax_id && entity?.manager?.name" class="text-slate-500">·</span>
          <span v-if="entity?.manager?.name">
            {{ capitalize(t('shared.manager', 0)) }}
            <RouterLink
              v-if="entity.manager.cid && can('admin', entity.manager.cid)"
              class="font-semibold text-white underline decoration-white/50 hover:text-slate-200 hover:decoration-slate-200/50"
              :to="investorPath(entity.manager.cid)"
            >
              {{ entity.manager.name }}
            </RouterLink>
            <span v-else class="font-semibold text-white">{{ entity.manager.name }}</span>
          </span>
        </div>
      </div>
      <div class="shrink-0">
        <div class="flex flex-wrap items-center justify-end gap-2">
          <slot name="actions"></slot>
        </div>
      </div>
    </div>
    <div class="mt-6">
      <slot name="tabs"></slot>
    </div>
  </header>
</template>
